import { Link } from "@tanstack/react-router";
import { MapPin, ArrowRight } from "lucide-react";
import {
  SERVICE_CITY_SERVICES,
  PRIORITY_CITIES,
  serviceCityHref,
  type ServiceCityService,
} from "@/data/serviceCityMatrix";

type ServiceKey = ServiceCityService["slug"];

const SERVICE_BY_SLUG: Record<ServiceKey, ServiceCityService> = Object.fromEntries(
  SERVICE_CITY_SERVICES.map((s) => [s.slug, s]),
) as Record<ServiceKey, ServiceCityService>;

interface ServiceCityLinksProps {
  service: ServiceKey;
  heading?: string;
  intro?: string;
}

/**
 * Prominent grid of links to /[service]/in/[city] landing pages. Drop into
 * service pages so search engines and shoppers can jump straight to the
 * city-specific variant of the service they're already reading about.
 */
export function ServiceCityLinks({ service, heading, intro }: ServiceCityLinksProps) {
  const meta = SERVICE_BY_SLUG[service];
  const label = meta.label;
  const cities = PRIORITY_CITIES;
  const headingText = heading ?? `${label} by City`;
  const introText =
    intro ??
    `Local install crews + free on-site measure. Pick your city for service details, response times, and recent projects nearby.`;

  return (
    <section
      aria-labelledby={`service-city-links-${service}`}
      className="border-t border-navy-deep/10 bg-stone-50 py-16"
    >
      <div className="mx-auto max-w-6xl px-4">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-red">
              Service Area
            </p>
            <h2
              id={`service-city-links-${service}`}
              className="mt-2 font-display text-3xl font-bold text-navy-deep sm:text-4xl"
            >
              {headingText}
            </h2>
            <p className="mt-3 max-w-2xl text-navy-deep/75">{introText}</p>
          </div>
        </div>

        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {cities.map((c) => (
            <li key={c.slug}>
              <Link
                to={serviceCityHref(service, c.slug)}
                className="group flex items-center justify-between gap-2 rounded-md border border-navy-deep/15 bg-white px-4 py-3 text-navy-deep transition hover:border-red hover:bg-red hover:text-white"
              >
                <span className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 shrink-0 text-red transition group-hover:text-white" />
                  <span className="font-display text-sm font-semibold">
                    {label} in {c.name}
                  </span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 opacity-0 transition group-hover:opacity-100" />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * Homepage variant: stacks every service-in-city grid so the index page links
 * out to every `/[service]/in/[city]` page in one block — shortens crawl path
 * from / to each service-in-city URL to a single hop.
 */
export function HomeServiceCityLinks() {
  return (
    <section
      aria-labelledby="home-service-city-links"
      className="border-t border-navy-deep/10 bg-stone-50 py-16"
    >
      <div className="mx-auto max-w-6xl px-4">
        <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-red">
          Find Your City
        </p>
        <h2
          id="home-service-city-links"
          className="mt-2 font-display text-3xl font-bold text-navy-deep sm:text-4xl"
        >
          Local service pages across Texas
        </h2>
        <p className="mt-3 max-w-2xl text-navy-deep/75">
          Jump straight to the city + service combo you need. Each page covers
          local install crews, response times, and recent projects nearby.
        </p>

        <nav aria-label="Service areas by city" className="mt-10 space-y-10">
          {SERVICE_CITY_SERVICES.map((s) => (
            <div key={s.slug}>
              <h3 className="font-display text-xl font-bold text-navy-deep">{s.label}</h3>
              <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {PRIORITY_CITIES.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={serviceCityHref(s.slug, c.slug)}
                      className="group flex items-center justify-between gap-2 rounded-md border border-navy-deep/15 bg-white px-4 py-3 text-navy-deep transition hover:border-red hover:bg-red hover:text-white"
                    >
                      <span className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 shrink-0 text-red transition group-hover:text-white" />
                        <span className="font-display text-sm font-semibold">
                          {s.label} in {c.name}
                        </span>
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 opacity-0 transition group-hover:opacity-100" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </div>
    </section>
  );
}

/**
 * Compact "Services in {City}" block — drop into a city page to link out to
 * every service-in-city URL for the current city.
 */
export function CityServiceCityLinks({ citySlug, cityName }: { citySlug: string; cityName: string }) {
  return (
    <section
      aria-labelledby={`city-services-${citySlug}`}
      className="border-t border-navy-deep/10 bg-cream py-12"
    >
      <div className="mx-auto max-w-6xl px-4">
        <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-red">
          Services in {cityName}
        </p>
        <h2
          id={`city-services-${citySlug}`}
          className="mt-2 font-display text-2xl font-bold text-navy-deep sm:text-3xl"
        >
          What we build in {cityName}
        </h2>
        <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICE_CITY_SERVICES.map((s) => (
            <li key={s.slug}>
              <Link
                to={serviceCityHref(s.slug, citySlug)}
                className="group flex items-center justify-between gap-2 rounded-md border border-navy-deep/15 bg-white px-4 py-3 text-navy-deep transition hover:border-red hover:bg-red hover:text-white"
              >
                <span className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 shrink-0 text-red transition group-hover:text-white" />
                  <span className="font-display text-sm font-semibold">
                    {s.label} in {cityName}
                  </span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 opacity-0 transition group-hover:opacity-100" />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
